import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Card from '@mui/material/Card';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import AlertTitle from '@mui/material/AlertTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { highlightQueryFromDsl } from 'src/utils/highlight-terms';

import { useTranslate } from 'src/locales';
import { BoolNode, SortOrder } from 'src/services/common.types';
import { ListEntityType } from 'src/services/lists/lists.types';
import { SortConfig, PageOptions } from 'src/services/api.types';
import { IOrgCandidateListItem } from 'src/services/candidates/candidates.types';
import { useSearchMyCandidatesLeanQuery, useUpdateCandidateRatingMutation } from 'src/services/candidates/candidates.service';

import Iconify from 'src/components/iconify';
import DataList from 'src/components/data-list/data-list';
import RecordPanel from 'src/components/record-panel/record-panel';
import { useRecordPanel } from 'src/components/record-panel/use-record-panel';
import DataListColumnPicker from 'src/components/data-list/data-list-column-picker';
import { useColumnPreferences } from 'src/components/data-list/use-column-preferences';

import AddItemsModal from 'src/sections/lists/modals/add-items-modal';
import BulkAddCandidatesJobModal from 'src/sections/jobs/modals/bulk-add-candidate-job-modal';

import { buildCandidateColumns } from '../table/candidate-columns';

const PAGE_SIZE = 50;

interface AdvancedSearchResultsProps {
  /** The combined DSL (keyword + structured criteria). */
  searchDsl: BoolNode;
  /** True once a search has been run — the query is skipped until then. */
  hasSearched: boolean;
  /** When sourcing for a job, bulk "Add to job" targets this job. */
  jobId?: string;
}

/**
 * Advanced-search results on the shared DataList (JT1-1532, item 32). Runs the DSL through the lean
 * read path (POST /my/candidates/list) so results are the same flat rows the candidates list renders
 * — configurable/sortable columns, per-column search, infinite scroll, and the record panel via the
 * leading eye (quick view) and the name (overlay). Selection drives bulk Add to job / Add to list.
 */
export default function AdvancedSearchResults({ searchDsl, hasSearched, jobId }: AdvancedSearchResultsProps) {
  const { t } = useTranslate();
  const tenant = useOrgTenant();
  const { openRecord } = useRecordPanel();

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<IOrgCandidateListItem[]>([]);
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<SortConfig>({ sort_by: 'last_activity_at', sort_order: SortOrder.DESC });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const pageOptions: PageOptions = useMemo(() => ({ page, per_page: PAGE_SIZE }), [page]);

  // A new DSL (a fresh search) resets pagination + accumulated rows.
  useEffect(() => { setPage(1); }, [searchDsl]);

  const { currentData, isFetching, error } = useSearchMyCandidatesLeanQuery(
    { search_dsl: searchDsl, page: pageOptions, sortConfig, column_filters: columnSearch },
    { skip: !hasSearched, refetchOnMountOrArgChange: true }
  );

  /**
   * A failed request is NOT an empty result (JT1-1647). On 2026-09-02 a role-title search in a
   * 38k-candidate org ran past the 20s statement ceiling, the endpoint returned 500, and this
   * component rendered "0 candidates found" — a confident, wrong statement about the recruiter's
   * own data, with 418 matching candidates sitting in the database.
   *
   * `hasSearched` plus an empty array therefore cannot be the only state: the rows are cleared so a
   * previous search's results are not passed off as this one's, and the list renders an error
   * instead of its empty state.
   */
  const failed = Boolean(error);

  useEffect(() => {
    if (!currentData) return;
    setRows((prev) => {
      if (page === 1) return currentData.results;
      const seen = new Set(prev.map((r) => r.id));
      return [...prev, ...currentData.results.filter((r) => !seen.has(r.id))];
    });
  }, [currentData, page]);

  useEffect(() => { if (error) setRows([]); }, [error]);

  const count = failed ? 0 : currentData?.count ?? 0;
  const hasMore = rows.length < count;

  const resetToFirstPage = useCallback(() => setPage(1), []);
  const onSortChange = useCallback((next: SortConfig) => { setSortConfig(next); resetToFirstPage(); }, [resetToFirstPage]);
  const onColumnSearchChange = useCallback((key: string, value: string) => {
    setColumnSearch((prev) => {
      const next = { ...prev };
      if (value) next[key] = value; else delete next[key];
      return next;
    });
    resetToFirstPage();
  }, [resetToFirstPage]);
  const onLoadMore = useCallback(() => { if (!isFetching && hasMore) setPage((p) => p + 1); }, [isFetching, hasMore]);

  const onSelectRow = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);
  const onSelectAll = useCallback((checked: boolean, ids: string[]) => setSelectedIds(checked ? Array.from(new Set(ids)) : []), []);

  const [updateRating] = useUpdateCandidateRatingMutation();
  const handleRatingUpdate = useCallback(async (candidateId: string, rating: number) => {
    const toastId = enqueueSnackbar(t('candidates.api.update_rating.loading'), { variant: 'loading', persist: true });
    try {
      await updateRating({ candidateId, rating }).unwrap();
      closeSnackbar(toastId);
      enqueueSnackbar(t('candidates.api.update_rating.success'), { variant: 'success' });
      setRows((prev) => prev.map((r) => (r.id === candidateId ? { ...r, rating } : r)));
    } catch {
      closeSnackbar(toastId);
      enqueueSnackbar(t('candidates.api.update_rating.default_error'), { variant: 'error' });
    }
  }, [t, updateRating]);

  // Every searched term (the keyword and the criteria rows) rides onto opened records as `?q=` so
  // their resume highlights it (item 18).
  const highlightQuery = useMemo(() => highlightQueryFromDsl(searchDsl), [searchDsl]);
  const handleQuickView = useCallback((id: string) => openRecord('candidate', id, { mode: 'quick', query: { q: highlightQuery } }), [openRecord, highlightQuery]);
  const handleOpenOverlay = useCallback((id: string) => openRecord('candidate', id, { mode: 'overlay', query: { q: highlightQuery } }), [openRecord, highlightQuery]);

  const registry = useMemo(
    () => buildCandidateColumns({ t, tenantType: tenant, onRatingUpdate: handleRatingUpdate, onOpenRecord: handleOpenOverlay }),
    [t, tenant, handleRatingUpdate, handleOpenOverlay]
  );
  const { columns, pickerModel, save } = useColumnPreferences('candidates', registry);

  const renderRowLeading = useCallback((row: IOrgCandidateListItem) => (
    <Tooltip title={t('common.quick_view', { defaultValue: 'Quick view' })}>
      <IconButton size="small" onClick={() => handleQuickView(row.id)}>
        <Iconify icon="solar:eye-bold" />
      </IconButton>
    </Tooltip>
  ), [handleQuickView, t]);

  const addToJob = useBoolean();
  const addToList = useBoolean();
  const onSuccessAddToList = useCallback(() => { setSelectedIds([]); addToList.onFalse(); }, [addToList]);
  const hasSelection = selectedIds.length > 0;

  const toolbar = (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
      <Typography variant="subtitle2">
        {failed ? '' : t('candidates.search.results', { count })}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        <DataListColumnPicker model={pickerModel} onSave={save} />
        <Button size="small" variant="soft" startIcon={<Iconify icon="mdi:invite" />} disabled={!hasSelection} onClick={addToJob.onTrue}>
          {t('common.add_to_job')}
        </Button>
        <Button size="small" variant="soft" startIcon={<Iconify icon="solar:list-bold" />} disabled={!hasSelection} onClick={addToList.onTrue}>
          {t('common.add_to_list', { defaultValue: 'Add to list' })}
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <>
      <Card sx={{ borderRadius: 1 }}>
        <DataList<IOrgCandidateListItem>
          columns={columns}
          rows={rows}
          getRowId={(r) => r.id}
          isSearching={isFetching}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          sort={sortConfig}
          onSortChange={onSortChange}
          columnSearch={columnSearch}
          onColumnSearchChange={onColumnSearchChange}
          toolbar={toolbar}
          selectable
          selectedIds={selectedIds}
          onSelectRow={onSelectRow}
          onSelectAll={onSelectAll}
          renderRowLeading={renderRowLeading}
          emptyLabel={hasSearched ? t('candidates.search.no_results') : t('candidates.search.initial_message')}
          errorContent={failed ? (
            <Alert severity="error" sx={{ my: 2 }}>
              <AlertTitle>{t('candidates.search.failed')}</AlertTitle>
              {t('candidates.search.failed_hint')}
            </Alert>
          ) : undefined}
        />
      </Card>

      <RecordPanel type="candidate" ids={rows.map((r) => r.id)} total={count} />

      <BulkAddCandidatesJobModal open={addToJob.value} onClose={addToJob.onFalse} candidates={selectedIds} jobId={jobId} />
      <AddItemsModal open={addToList.value} onClose={addToList.onFalse} onSuccess={onSuccessAddToList} items={selectedIds} entityType={ListEntityType.CANDIDATE} />
    </>
  );
}
